/**
 * Phase 1 keyword-picker — "Track selected" dialog.
 *
 * Consumes an array of {keywordId, keywordRunId, term} candidates (the
 * caller decides what to bulk-track — top-N by opportunity, an entire
 * cluster, filtered results, etc.) and lets the user prune before hitting
 * the bulk endpoint. Also accepts an optional shared target_url.
 *
 * Renders inline "created" / "skipped" summary after submit so the user
 * knows which items landed and which were already tracked.
 */
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { TrackedKeywordsBulkSkipReason } from '@seo/types';
import { Button } from '@/components/ui/button';
import { FormDialog } from '@/components/ui/form-dialog';
import { FormField, TextField } from '@/components/ui/form-field';
import { summarizeApiError } from '@/lib/api';
import { useCreateTrackedKeywordsBulk } from '@/features/ranks/use-create-tracked-keywords-bulk';

export interface BulkCandidate {
  keywordId: string;
  keywordRunId: string;
  term: string;
}

export function TrackKeywordsBulkDialog({
  open,
  onOpenChange,
  projectId,
  candidates,
  title = 'Track selected keywords',
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  candidates: BulkCandidate[];
  title?: string;
}): JSX.Element {
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [targetUrl, setTargetUrl] = useState('');
  const bulk = useCreateTrackedKeywordsBulk();

  const kept = useMemo(
    () => candidates.filter((c) => !removed.has(c.keywordId)),
    [candidates, removed],
  );

  const submit = async () => {
    if (kept.length === 0) return;
    try {
      const result = await bulk.mutateAsync({
        project_id: projectId,
        items: kept.map((c) => ({
          keyword: c.term,
          keyword_id: c.keywordId,
          keyword_run_id: c.keywordRunId,
          target_url: targetUrl.trim() ? targetUrl.trim() : null,
        })),
      });
      const createdCount = result.created.length;
      const skippedCount = result.skipped.length;
      if (skippedCount === 0) {
        toast.success(`Now tracking ${createdCount} keyword${createdCount === 1 ? '' : 's'}`);
      } else {
        toast.success(`Tracking ${createdCount} · skipped ${skippedCount}`, {
          description: summarizeSkipped(result.skipped),
        });
      }
      setRemoved(new Set());
      setTargetUrl('');
      onOpenChange(false);
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: 'Could not bulk-track keywords' });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={<span />}
      title={title}
      description="Every keyword below will be added to Tracking. Remove any you don't want. Duplicates are silently skipped."
      submitLabel={`Track ${kept.length} keyword${kept.length === 1 ? '' : 's'}`}
      submitting={bulk.isPending}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <FormField label="Keywords to track" hint={`${kept.length} selected${removed.size > 0 ? ` · ${removed.size} removed` : ''}`}>
        {kept.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            All candidates removed. Close and try again.
          </div>
        ) : (
          <ul className="max-h-64 overflow-y-auto rounded-md border">
            {kept.map((c) => (
              <li
                key={c.keywordId}
                className="flex items-center gap-2 border-b px-3 py-2 last:border-0"
              >
                <span className="min-w-0 flex-1 truncate text-sm">{c.term}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${c.term}`}
                  onClick={() => setRemoved((prev) => new Set(prev).add(c.keywordId))}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </FormField>

      <TextField
        label="Shared page URL (optional)"
        placeholder="https://example.com/blog/post"
        value={targetUrl}
        onChange={(e) => setTargetUrl(e.target.value)}
        hint="Applied to every keyword. Blank = any page on your domain."
      />
    </FormDialog>
  );
}

function summarizeSkipped(
  skipped: { keyword: string; reason: TrackedKeywordsBulkSkipReason }[],
): string {
  const counts = { already_tracked: 0, keyword_reference_invalid: 0, over_limit: 0 };
  for (const s of skipped) counts[s.reason] += 1;
  const parts: string[] = [];
  if (counts.already_tracked > 0) parts.push(`${counts.already_tracked} already tracked`);
  if (counts.keyword_reference_invalid > 0)
    parts.push(`${counts.keyword_reference_invalid} invalid reference`);
  if (counts.over_limit > 0) parts.push(`${counts.over_limit} hit your plan limit`);
  return parts.join(' · ');
}
